import { zodResolver } from "@hookform/resolvers/zod";
import { ReactElement, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useBindCustomerMutation, useEditProjectMutation, useFindOneProjectQuery } from "../../../../features/projectApi";
import { addBannerWithTimeout } from "../../../lib/centralizedBanner/slice";
import { keycloak } from "../../../lib/keycloak";
import { useAppDispatch, useAppSelector } from "../../../lib/redux/hooks";
import { SettingsIcon } from "../../../ui/icons/settings";
import { Layout } from "../../../ui/layout";
import Button from "../../../ui/molecules/button";
import { Spinner } from "../../../ui/molecules/spinner";
import { MenuItems } from "../../../ui/organisms/navbar/dto";
import { TabMenu } from "../../../ui/organisms/tabs/tabMenu";
import { TabPages } from "../../../ui/organisms/tabs/tabPages";
import {
  findClients
} from "../../users/slice";
import { EditProjectPage } from "../dto";
import { EmailSettingModal } from "../mails/emailSettingModal";
import { setEmailSettingsModalOpen } from "../mails/slice";
import {
  setEditProjectPage
} from "../slice";
import { AddTargetList } from "./addTargetList";
import { Form } from "./form";
import { TargetLists } from "./targetLists";
import { BindCustomerFormData, bindCustomerSchema, ProjectEditFormData, projectEditSchema } from "./validation";

export function EditProject() {
  const { projectId } = useParams();
  const { data: projectData, isLoading: isLoadingProject } = useFindOneProjectQuery({ projectId: projectId! }, {
    skip: projectId === undefined
  });
  const [bindCustomer] = useBindCustomerMutation()

  const form = useForm<ProjectEditFormData>({
    resolver: zodResolver(projectEditSchema),
    defaultValues: {
      description: "",
      commercialIds: [],
    }
  })

  const customerBind = useForm<BindCustomerFormData>({
    resolver: zodResolver(bindCustomerSchema),
    defaultValues: {
      customerId: ""
    }
  })

  const [editProject] = useEditProjectMutation()
  const handleEditProject = async () => {
    try {
      await editProject({ id: projectId!, data: form.getValues() }).unwrap()
      dispatch(addBannerWithTimeout({
        variant: "success",
        message: "Progetto aggiornato con successo"
      }))
    } catch (error) {
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Errore durante l'aggiornamento del progetto"
      }))
    }
  }

  useEffect(() => {
    if (projectData) {
      const formValues: Partial<ProjectEditFormData> = {
        description: projectData.description ?? undefined,
        commercialIds: projectData.commercials?.map(commercial => commercial.id) ?? [],
      };
      form.reset(formValues);
    }
  }, [projectData, form]);

  const handleCustomerBindChange = async () => {
    try {
      await bindCustomer({ id: projectData?.id!, data: { customerId: customerBind.getValues().customerId! } }).unwrap()
      dispatch(addBannerWithTimeout({
        variant: "success",
        message: "Cliente associato con successo"
      }))
    } catch (error) {
      dispatch(addBannerWithTimeout({
        variant: "error",
        message: "Errore durante l'associazione del cliente"
      }))
    }
  };

  const usersState = useAppSelector((state) => state.users);
  const projectsState = useAppSelector((state) => state.projects);
  const dispatch = useAppDispatch();

  const [direction, setDirection] = useState<"left" | "right">("left");
  const [tabValue, setTabValue] = useState<number>(
    window.location.pathname.includes("specifics") ? 0 : 1
  );

  useEffect(() => {
    if (!keycloak.hasRealmRole('customer')) {
      dispatch(findClients());
    }
    if (window.location.pathname.includes("specifics")) {
      setTabValue(0);
    }
    if (window.location.pathname.includes("target-lists")) {
      setTabValue(1);
      if (window.location.pathname.split("/")[4]) {
        dispatch(setEditProjectPage(EditProjectPage.EDITLIST));
      }
    }
  }, []);

  const editProjectMap = new Map<EditProjectPage, ReactElement>([
    [EditProjectPage.LIST, <TargetLists />],
    [EditProjectPage.ADDLIST, <AddTargetList project={projectData!} />],
    // [EditProjectPage.EDITLIST,
    // <EditTargetList
    //   // form={form}
    //   // targetContactIds={contactsIds ?? []}

    //   // peopleContacts={contactPeopleData as unknown as FindContactsTargetResponse<ContactType.PERSON>}
    //   // companiesContacts={contactCompaniesData as unknown as FindContactsTargetResponse<ContactType.COMPANY>}

    //   // peopleCounters={counters?.find(count => count.contactType === ContactType.PERSON)!}
    //   // companiesCounters={counters?.find(count => count.contactType === ContactType.COMPANY)!}

    //   // companiesFilters={companiesFilters}
    //   // setCompaniesFilters={act => setCompaniesFilters({ ...companiesFilters, ...act })}
    //   // peopleFilters={peopleFilters}
    //   // setPeopleFilters={act => setPeopleFilters((prev) => ({ ...prev, ...act }))}

    //   // selectedIdsPeople={selectedPeopleIds}
    //   // setSelectedIdsPeople={arg => setSelectedPeopleIds(arg)}
    //   // setShowPopupPeople={arg => setShowPopupPeople(arg)}
    //   // showPopupPeople={showPopupPeople}
    //   // selectedIdsCompanies={selectedCompaniesIds}
    //   // setSelectedIdsCompanies={arg => setSelectedCompaniesIds(arg)}
    //   // setShowPopupCompanies={arg => setShowPopupCompanies(arg)}
    //   // showPopupCompanies={showPopupCompanies}
    //   // //TODO LOAD TO ACTIVATE
    //   // toActivatePeople={0}
    //   // toActivateCompanies={0}

    //   targetList={null as unknown as TargetListDTO}
    //   toActivate={peopleData?.toActivate || 0}
    //   onChangeFilters={setPeopleFilters}
    //   reloadCompanies={() => companiesFilters.findContacts()}
    //   reloadPeople={() => { }}
    //   peopleStatus={isLoadingPeople ? "loading" : "idle"}
    //   peopleContacts={peopleData?.people as unknown as FindContactsTargetResponse<ContactType.PERSON>}
    //   companiesContacts={companiesFilters.contacts as unknown as FindContactsTargetResponse<ContactType.COMPANY>}
    //   companiesStatus={companiesFilters.status}
    //   companiesFilters={companiesFilters.filters}
    //   setCompaniesFilters={function (action: Partial<FindAllContactsFilters>): void {
    //     companiesFilters.updateFilters(action);
    //   }}
    //   setCompaniesPage={function (action: number): void { companiesFilters.changePage(action); }}
    //   resetCompaniesFilters={function (): void { companiesFilters.resetAllFilters() }}
    //   selectedIdsPeople={selectedPeopleIds}
    //   setSelectedIdsPeople={arg => setSelectedPeopleIds(arg)}
    //   setShowPopupPeople={arg => setShowPopupPeople(arg)}
    //   showPopupPeople={showPopupPeople}
    //   selectedIdsCompanies={selectedCompaniesIds}
    //   setSelectedIdsCompanies={arg => setSelectedCompaniesIds(arg)}
    //   setShowPopupCompanies={arg => setShowPopupCompanies(arg)}
    //   showPopupCompanies={showPopupCompanies}
    //   peopleFilters={peopleFilters}
    //   setPeopleFilters={function (action: Partial<FindAllContactsFilters>): void {

    //   }}
    //   setPeoplePage={function (action: number): void {
    //     // peopleFilters.changePage(action);
    //   }}
    //   resetPeopleFilters={function (): void {
    //     // peopleFilters.resetAllFilters()
    //   }} />],
  ]);

  const tabValuePathMap = new Map<number, string>([
    [0, "/specifics"],
    [1, "/target-lists"],
  ]);

  const navigate = useNavigate();

  if (isLoadingProject) {
    return <div className="flex items-center justify-center h-screen w-full"><Spinner /></div>
  }

  return (
    <Layout
      menuItem={MenuItems.PROJECTS}
      breadcrumbItems={["Progetti", "Modifica progetto"]}
      headerLabel={projectData?.name!}
      headerBackPath={"/projects"}
      headerChildren={
        <div className="flex items-center h-[100%] w-[100%] justify-between">
          <TabMenu
            direction={direction}
            setDirection={(value) => setDirection(value)}
            setValue={(e) => {
              setTabValue(e);
              navigate(
                "/edit-project/" +
                window.location.pathname.split("/")[2] +
                tabValuePathMap.get(e)
              );
              if (e === 1) {
                dispatch(setEditProjectPage(EditProjectPage.LIST));
              }
            }}
            value={tabValue}
            items={[
              {
                icon: <></>,
                label: "Specifiche",
              },
              {
                icon: <></>,
                label:
                  "Liste target (" +
                  (projectData?.targetListsIds?.length ?? 0) +
                  ")",
              },
            ]}
          />
          {
            (tabValue !== 1 || !window.location.pathname.split('/')[4]) && !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator')) && projectsState.editProjectPage !== EditProjectPage.ADDLIST &&
            <div className="flex flex-row gap-2">
              <Button
                onClick={() => dispatch(setEmailSettingsModalOpen(true))}
                size={"sm"}
                label="Impostazioni email"
                iconPosition={"left"}
                icon={<SettingsIcon color={""} size={0} />}
                variant={"link"}
                color={"blue"}
              />
              {
                ((keycloak.hasRealmRole('commercial') && (usersState.findMeResponse?.isAutonomous || usersState.findMeResponse?.isAutonomous === null)) || !keycloak.hasRealmRole('commercial')) &&
                <Button
                  size={"sm"}
                  iconPosition={"off"}
                  variant={"outline"}
                  color={"blue"}
                  label="Aggiungi lista target"
                  onClick={() => {
                    setTabValue(1)
                    dispatch(setEditProjectPage(EditProjectPage.ADDLIST));
                  }}
                />
              }
              {
                tabValue === 0 &&
                !(keycloak.hasRealmRole('customer') || keycloak.hasRealmRole('collaborator'))
                // &&
                // (
                //   (
                //     form.formState?.defaultValues?.commercialIds !== form.watch('commercialIds') ||
                //     form.formState?.defaultValues?.description !== form.watch('description') ||
                //     form.formState?.defaultValues?.emailFrom !== form.watch('emailFrom') ||
                //     form.formState?.defaultValues?.nameFrom !== form.watch('nameFrom')
                //   ) 
                // )
                &&
                <Button
                  size={"sm"}
                  iconPosition={"off"}
                  label="Modifica progetto"
                  variant={"solid"}
                  color={"blue"}
                  onClick={() => {
                    if (customerBind.watch('customerId')) {
                      customerBind.handleSubmit(handleCustomerBindChange)()
                    }
                    form.handleSubmit(handleEditProject)()
                  }}
                />
              }
            </div>
          }
        </div>
      }
    >
      <EmailSettingModal project={projectData!} />
      {
        window.location.pathname.includes("edit-project") && !keycloak.hasRealmRole('customer') &&
        // <AssociateCompanyModal /> TODO see if needed
        null
      }
      <div style={{ height: window.location.pathname.split('/')[4] ? 'calc(100% - 64px)' : '100%' }}>
        <TabPages
          direction={direction}
          setDirection={(value) => setDirection(value)}
          pages={[
            <Form key={0} project={projectData!} form={form} bindCustomer={customerBind} />,
            <div key={1} className="max-h-full h-full">
              {editProjectMap.get(projectsState.editProjectPage)}
            </div>,
          ]}
          value={tabValue}
          setValue={(e) => {
            setTabValue(e);
          }}
        />
      </div>
    </Layout>
  );
}
